import {
  Practice,
  Location,
  VirtualLocation,
  LocationTypeDisplayId,
} from "./schema"

import { PublicProfilePractice } from "../Profile/schema"

export const CALIFORNIA = "California"

// Only considers physical office locations - the billing address is ignored.
export const hasCaliforniaOfficeLocation = (
  practice: Practice | PublicProfilePractice,
): boolean =>
  (practice.locations || []).some(
    (physicalLocation) => physicalLocation.stateOrTerritory === CALIFORNIA,
  )

// Standard Practice Agreement required for all practices (US 20046).
// Pass skipCaliforniaExclusion=true to include CA practices (behind feature flag).
export const hasCaliforniaAddressOrLocation = (
  practice: Practice | PublicProfilePractice,
): boolean => {
  const practiceStateOrTerritory =
    "practiceInfo" in practice
      ? practice.practiceInfo?.practiceAddress?.stateOrTerritory
      : practice.billingAddress?.stateOrTerritory

  const hasCaliforniaAddress =
    practiceStateOrTerritory === CALIFORNIA ||
    hasCaliforniaOfficeLocation(practice)

  return hasCaliforniaAddress
}

export const practiceAgreementSignatureRequired = (
  practice: Practice | PublicProfilePractice,
  {
    skipCaliforniaExclusion = false,
  }: { skipCaliforniaExclusion?: boolean } = {},
): boolean => {
  const californiaLawsApply =
    !!practice.hasOnlineServicesInCalifornia ||
    hasCaliforniaOfficeLocation(practice)

  let knoxKeeneAgreementNeedsSigned = false
  let practiceAgreementNeedsSigned = false

  if ("practiceInfo" in practice) {
    knoxKeeneAgreementNeedsSigned =
      (!!practice.isKnoxKeeneStale ||
        !practice.practiceAgreement?.knoxKeeneAgreement?.signedFileId) &&
      californiaLawsApply

    practiceAgreementNeedsSigned =
      (!!practice.isPracticeAgreementStale ||
        !practice.practiceAgreement?.practiceAgreement?.signedFileId) &&
      (skipCaliforniaExclusion ||
        practice.practiceInfo?.practiceAddress?.stateOrTerritory !== CALIFORNIA)
  } else {
    knoxKeeneAgreementNeedsSigned =
      (!!practice.isKnoxKeeneStale || !practice.isKnoxKeeneSigned) &&
      californiaLawsApply

    practiceAgreementNeedsSigned =
      (!!practice.isPracticeAgreementStale ||
        !practice.isPracticeAgreementSigned) &&
      (skipCaliforniaExclusion ||
        practice.billingAddress?.stateOrTerritory !== CALIFORNIA)
  }

  return knoxKeeneAgreementNeedsSigned || practiceAgreementNeedsSigned
}

export const getCurrentFormattedDateForESign = () =>
  new Date().toLocaleDateString("en-us", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })

/**
 * Syncs the location photos between the updated practice and the current selected location.
 * This is necessary because the location photos are stored in multiple places in the Redux state,
 * and we need to ensure they are consistent.
 *
 * NOTE: the locationPhotos state currently stores the dynamically hydrated practice location photos,
 * while the Location.photos stores the cached photos for that location, selectedLocation stores the
 * dynamically selected locationId url param, which is used to identify the currently selected location.
 * Ideally, we would use one source of truth - practice.locations.photos and drive currently selected
 * location from the url against said source of truth, but we cannot risk regression.
 */
export const syncLocationPhotos = (
  updatedPractice: Practice,
  currentSelectedLocation: VirtualLocation | Location | null,
): {
  updatedPractice: Practice
} => {
  // Skip if it's virtual or a location isnt selected (eg initial load on a non-office deep link page)
  if (
    currentSelectedLocation?.locationTypeDisplayId ===
      LocationTypeDisplayId.VIRTUAL ||
    !currentSelectedLocation ||
    !currentSelectedLocation.id
  ) {
    return {
      updatedPractice,
    }
  }

  // Sync the cached location photos with the hydrated practice location photos so we can default to them rather than refetching every time
  // NOTE: rely on the locationPhotos field if it exists (eg after an upload), otherwise fall back to the practice.locations.photos
  //       because the locationPhotos is where the hydrated base64 images and full user name are stored after an upload.

  // Check if the selected location still exists in the updated practice (it may have been deleted)
  const locationExistsInPractice = updatedPractice.locations.some(
    (loc) => loc.id === currentSelectedLocation.id,
  )

  // If the location was deleted, just return the updated practice as-is
  if (!locationExistsInPractice) {
    return {
      updatedPractice,
    }
  }

  const hydratedLocationPhotos =
    updatedPractice.locationPhotos.length > 0
      ? updatedPractice.locationPhotos
      : (updatedPractice.locations.find((location) => {
          return location.id === currentSelectedLocation.id
        })?.photos ?? [])
  const updatedCachedLocation = {
    ...currentSelectedLocation,
    photos: hydratedLocationPhotos,
  }
  const updatedCachedLocations = updatedPractice.locations.filter(
    (loc: Location) => loc.id !== currentSelectedLocation.id,
  )
  updatedCachedLocations.push(updatedCachedLocation)

  return {
    updatedPractice: {
      ...updatedPractice,
      locations: updatedCachedLocations,
    },
  }
}
